import axios from 'axios'
import type { AxiosError, AxiosInstance } from 'axios'

import { env } from '../lib/env'
import { getSuiteLandingLoginUrl, getSuiteLandingSessionExpiredUrl } from '../sso/sso-constants'
import { clearSuiteAccessTokenCache } from '../sso/services/suite-access-token.service'
import { clearPayAccessTokenCache } from '../sso/services/pay-auth-bridge.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { logError, logInfo, logRedirect, logWarn } from '../sso/utils/sso-logger'

let isRefreshing = false

let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown = null) => {
  logInfo('refresh-interceptor — processQueue', {
    queueLength: failedQueue.length,
    failed: Boolean(error)
  })
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve()
    }
  })

  failedQueue = []
}

/**
 * Token refresh + 401 handling (mirrors adamo-id-front-end `refresh-interceptor.ts`).
 */
export function attachRefreshInterceptor(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ errors?: string[] }>) => {
      const originalRequest = error.config as typeof error.config & { _retry?: boolean }
      const requestUrl = `${originalRequest?.baseURL ?? ''}${originalRequest?.url ?? ''}`

      const is401Error = error.response?.status === 401
      const isAccessTokenExpired = error.response?.data?.errors?.includes('ACCESS_TOKEN_EXPIRED')

      logWarn('refresh-interceptor — response error', {
        requestUrl,
        status: error.response?.status,
        is401Error,
        isAccessTokenExpired,
        errors: error.response?.data?.errors,
        alreadyRetried: originalRequest?._retry,
        isRefreshing
      })

      if (is401Error && !isAccessTokenExpired && !originalRequest._retry) {
        originalRequest._retry = true

        processQueue(error)
        isRefreshing = false

        if (isSuiteSsoRedirectMode()) {
          const url = getSuiteLandingLoginUrl()
          logRedirect('401 (not ACCESS_TOKEN_EXPIRED) — redirect to suite login', url, { requestUrl })
          window.location.href = url
        } else if (window.location.pathname !== '/dev/login') {
          logRedirect('401 — redirect to /dev/login', '/dev/login', { requestUrl })
          window.location.href = '/dev/login'
        }

        return Promise.reject(error)
      }

      if (isAccessTokenExpired && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          logInfo('refresh-interceptor — ACCESS_TOKEN_EXPIRED, queueing request', { requestUrl })
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => client(originalRequest!))
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        logInfo('refresh-interceptor — ACCESS_TOKEN_EXPIRED, calling POST /api/v1/auth/refresh', {
          requestUrl,
          apiBase: env.REACT_APP_ADAMO_API_BASE_URL
        })

        try {
          await axios.post(`${env.REACT_APP_ADAMO_API_BASE_URL}/api/v1/auth/refresh`, {}, { withCredentials: true })

          clearSuiteAccessTokenCache()
          clearPayAccessTokenCache()

          logInfo('refresh-interceptor — refresh SUCCESS, retrying original request')
          processQueue()
          isRefreshing = false

          return client(originalRequest)
        } catch (refreshError) {
          logError('refresh-interceptor — refresh FAILED', refreshError, { requestUrl })
          processQueue(refreshError)
          isRefreshing = false

          if (isSuiteSsoRedirectMode()) {
            const url = getSuiteLandingSessionExpiredUrl()
            logRedirect('Refresh failed — redirect to suite (session_expired)', url, { requestUrl })
            window.location.href = url
          } else if (window.location.pathname !== '/dev/login') {
            logRedirect('Refresh failed — redirect to /dev/login', '/dev/login')
            window.location.href = '/dev/login'
          }

          return Promise.reject(refreshError)
        }
      }

      return Promise.reject(error)
    }
  )
}
